import React, { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";

// Lazy imports for pages
const Index = React.lazy(() => import("./pages/Index"));
const AuthPage = React.lazy(() => import("./pages/AuthPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));
const ClubDashboard = React.lazy(() => import("./pages/ClubDashboard"));
const AcceptInvitation = React.lazy(() => import("./pages/AcceptInvitation"));

const queryClient = new QueryClient();

// Page loading spinner component that matches existing auth loading design
const PageLoadingSpinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-2">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const AuthPageRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-2">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return (
    <Suspense fallback={<PageLoadingSpinner />}>
      <AuthPage />
    </Suspense>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route 
              path="/" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingSpinner />}>
                    <Index />
                  </Suspense>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/club-dashboard" 
              element={
                <ProtectedRoute>
                  <Suspense fallback={<PageLoadingSpinner />}>
                    <ClubDashboard />
                  </Suspense>
                </ProtectedRoute>
              } 
            />
            <Route path="/auth" element={<AuthPageRoute />} />
            <Route path="/accept-invitation/:invitationId" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <AcceptInvitation />
              </Suspense>
            } />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={
              <Suspense fallback={<PageLoadingSpinner />}>
                <NotFound />
              </Suspense>
            } />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
